import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, normalizeStyle as _normalizeStyle, Transition as _Transition, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "flex items-center gap-2 min-w-0" }
const _hoisted_2 = { class: "truncate" }

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { CheckIcon, ChevronDownIcon } from '@heroicons/vue/20/solid'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useDropdownAnchor } from '@/composables/useDropdownAnchor'
import { PAGE_TABS_MORE, pageTabTarget } from '@/config/tours'

const MORE_FALLBACK = 88
const SAFETY = 4


export default {
  __name: 'NeueTabs',
  props: {
  tabs: { type: Array, default: () => [] },
  modelValue: { type: [String, Number], default: null }
},
  emits: ['update:modelValue'],
  setup(__props, { emit: __emit }) {

const style = {
  wrap: 'flex items-start min-w-0 w-full gap-1 pt-1 -ml-2',
  list: 'min-w-0 flex overflow-hidden items-start text-sm tracking-tight',
  tab: 'flex-shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap px-2 py-1 rounded-md',
  active: 'text-gray-900 font-semibold bg-slate-200',
  inactive: 'text-gray-600',
  overflow: 'flex-shrink-0 self-start',
  more: 'inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-sm tracking-tight text-gray-600 hover:bg-slate-200 hover:text-gray-900',
  moreActive: 'bg-slate-200 text-gray-900 font-semibold'
}

const props = __props
const emit = __emit

const route = useRoute()

const { setReference, setFloating, floatingStyles } = useDropdownAnchor({ placement: 'bottom-end' })

const host = ref(null)
const wrap = ref(null)
const list = ref(null)
const more = ref(null)

const setMore = el => {
  more.value = el?.$el ?? el ?? null
}

const extents = ref([])
const moreWidth = ref(MORE_FALLBACK)
const visibleCount = ref(Number.MAX_SAFE_INTEGER)

const visibleTabs = computed(() => props.tabs.slice(0, visibleCount.value))
const hiddenTabs = computed(() => props.tabs.slice(visibleCount.value))
const overflowing = computed(() => hiddenTabs.value.length > 0)
const activeHidden = computed(() => hiddenTabs.value.some(isActive))

const chromeWidth = () => {
  const el = wrap.value
  if (!el || typeof getComputedStyle !== 'function') return 0
  const cs = getComputedStyle(el)
  const sum = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth'].reduce(
    (total, key) => total + (parseFloat(cs[key]) || 0),
    0
  )
  return sum
}

const measureTabs = () => {
  const kids = list.value ? Array.from(list.value.children) : []
  if (!kids.length) return
  const base = kids[0].getBoundingClientRect().left
  extents.value = kids.map(kid => {
    const box = kid.getBoundingClientRect()
    return box.left - base + box.width
  })
}

const fit = () => {
  const el = host.value
  if (!el) return
  if (visibleCount.value >= props.tabs.length) measureTabs()
  if (more.value) moreWidth.value = more.value.offsetWidth || moreWidth.value
  const ext = extents.value
  if (ext.length !== props.tabs.length) return

  const available = el.clientWidth - chromeWidth()
  if (ext[ext.length - 1] <= available) {
    visibleCount.value = props.tabs.length
    return
  }

  const room = available - moreWidth.value - SAFETY
  let count = 0
  while (count < ext.length && ext[count] <= room) count++
  visibleCount.value = Math.max(count, 1)
}

let observer = null
let frame = null

const schedule = () => {
  if (frame !== null) return
  frame = requestAnimationFrame(() => {
    frame = null
    fit()
  })
}

const attach = () => {
  observer?.disconnect()
  observer = null
  if (!host.value || typeof ResizeObserver === 'undefined') return
  observer = new ResizeObserver(schedule)
  observer.observe(host.value)
}

const reset = async () => {
  visibleCount.value = Number.MAX_SAFE_INTEGER
  extents.value = []
  await nextTick()
  fit()
  attach()
}

onMounted(reset)

onBeforeUnmount(() => {
  observer?.disconnect()
  if (frame !== null) cancelAnimationFrame(frame)
})

watch(() => props.tabs, reset, { deep: true })

watch(overflowing, async isOverflowing => {
  if (!isOverflowing) return
  await nextTick()
  fit()
})

const siblingQueryKeys = computed(() =>
  props.tabs.flatMap(tab => (tab.to?.includes('?') ? Array.from(new URLSearchParams(tab.to.split('?')[1]).keys()) : []))
)

const matchesRoute = to => {
  const [path, query] = to.split('?')
  if (route.path !== path) return false
  if (!query) return siblingQueryKeys.value.every(key => route.query[key] === undefined)
  return Array.from(new URLSearchParams(query).entries()).every(([key, value]) => route.query[key] === value)
}

function isActive(tab) {
  if ('active' in tab) return Boolean(tab.active)
  if (props.modelValue !== null) return tab.id === props.modelValue
  if (tab.match) return tab.match.some(path => route.path === path || route.path.startsWith(path + '/'))
  return tab.to ? matchesRoute(tab.to) : false
}

const select = tab => {
  tab.action?.()
  if (!tab.to) emit('update:modelValue', tab.id)
}

return (_ctx, _cache) => {
  return (props.tabs?.length)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "host",
        ref: host,
        class: "w-full min-w-0"
      }, [
        _createElementVNode("div", {
          ref_key: "wrap",
          ref: wrap,
          class: _normalizeClass(style.wrap)
        }, [
          _createElementVNode("div", {
            ref_key: "list",
            ref: list,
            class: _normalizeClass(style.list),
            role: "tablist"
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleTabs.value, (tab) => {
              return (_openBlock(), _createBlock(_resolveDynamicComponent(tab.to ? 'router-link' : 'button'), {
                id: 'section-' + tab.id,
                key: tab.id,
                to: tab.to,
                "data-tour": _unref(pageTabTarget)(tab.id),
                role: "tab",
                "aria-selected": isActive(tab),
                class: _normalizeClass([style.tab, isActive(tab) ? style.active : style.inactive]),
                onClick: $event => (select(tab))
              }, {
                default: _withCtx(() => [
                  (tab.icon)
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(tab.icon), {
                        key: 0,
                        class: "h-4 w-4 shrink-0",
                        "aria-hidden": "true"
                      }))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", null, _toDisplayString(tab.name), 1)
                ]),
                _: 2
              }, 1032, ["id", "to", "data-tour", "aria-selected", "class", "onClick"]))
            }), 128))
          ], 2),
          (overflowing.value)
            ? (_openBlock(), _createBlock(_unref(Menu), {
                key: 0,
                ref: setMore,
                as: "div",
                class: _normalizeClass(style.overflow)
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(MenuButton), {
                    ref: _unref(setReference),
                    "data-tour": _unref(PAGE_TABS_MORE),
                    "data-hidden-tabs": hiddenTabs.value.map(tab => tab.id).join(' '),
                    class: _normalizeClass([style.more, activeHidden.value ? style.moreActive : ''])
                  }, {
                    default: _withCtx(() => [
                      _cache[0] || (_cache[0] = _createElementVNode("span", null, "More", -1)),
                      _createVNode(_unref(ChevronDownIcon), {
                        class: "h-4 w-4 shrink-0",
                        "aria-hidden": "true"
                      })
                    ]),
                    _: 1
                  }, 8, ["data-tour", "data-hidden-tabs", "class"]),
                  (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
                    _createVNode(_Transition, {
                      "enter-active-class": "transition ease-out duration-100",
                      "enter-from-class": "transform opacity-0 scale-95",
                      "enter-to-class": "transform opacity-100 scale-100",
                      "leave-active-class": "transition ease-in duration-75",
                      "leave-from-class": "transform opacity-100 scale-100",
                      "leave-to-class": "transform opacity-0 scale-95"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(MenuItems), {
                          ref: _unref(setFloating),
                          style: _normalizeStyle(_unref(floatingStyles)),
                          class: "z-50 w-60 max-h-80 overflow-y-auto py-1 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                        }, {
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.tabs, (tab) => {
                              return (_openBlock(), _createBlock(_unref(MenuItem), {
                                key: tab.id
                              }, {
                                default: _withCtx(({ active }) => [
                                  (_openBlock(), _createBlock(_resolveDynamicComponent(tab.to ? 'router-link' : 'button'), {
                                    to: tab.to,
                                    class: _normalizeClass(["flex items-center justify-between gap-2 px-4 py-2 text-sm w-full text-left", [
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                    isActive(tab) ? 'font-semibold text-gray-900' : ''
                  ]]),
                                    onClick: $event => (select(tab))
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("span", _hoisted_1, [
                                        (tab.icon)
                                          ? (_openBlock(), _createBlock(_resolveDynamicComponent(tab.icon), {
                                              key: 0,
                                              class: "h-4 w-4 shrink-0",
                                              "aria-hidden": "true"
                                            }))
                                          : _createCommentVNode("", true),
                                        _createElementVNode("span", _hoisted_2, _toDisplayString(tab.name), 1)
                                      ]),
                                      (isActive(tab))
                                        ? (_openBlock(), _createBlock(_unref(CheckIcon), {
                                            key: 0,
                                            class: "h-4 w-4 shrink-0",
                                            "aria-hidden": "true"
                                          }))
                                        : _createCommentVNode("", true)
                                    ]),
                                    _: 2
                                  }, 1032, ["to", "class", "onClick"]))
                                ]),
                                _: 2
                              }, 1024))
                            }), 128))
                          ]),
                          _: 1
                        }, 8, ["style"])
                      ]),
                      _: 1
                    })
                  ]))
                ]),
                _: 1
              }, 8, ["class"]))
            : _createCommentVNode("", true)
        ], 2)
      ], 512))
    : _createCommentVNode("", true)
}
}

}